import { Injectable } from '@angular/core'
import { Action, Selector, State, StateContext } from '@ngxs/store'
import { tap } from 'rxjs/operators'
import { CallIntelligenceStateModel } from './call-intelligence.model'
import { CallIntelligenceService } from '../call-intelligence.service'
import {
  CreateCallTag,
  DeleteCallTag,
  EditCallIntelligenceLeadAnalysis,
  GetActiveCallTags,
  GetCallIntelligenceDashboard,
  GetCallIntelligenceDrilldown,
  GetCallIntelligenceLeadAnalysisVersions,
  GetCallIntelligenceLeadDetails,
  GetCallIntelligenceLeadsList,
  GetCallTags,
  GetFilteredCallTags,
  LinkCallIntelligenceLeadContact,
  MarkCallIntelligenceLeadReviewed,
  ReProcessCallIntelligenceLead,
  RetagCalls,
  UnlinkCallIntelligenceLeadContact,
  UpdateCallTag
} from './call-intelligence.actions'

@State<CallIntelligenceStateModel>({
  name: 'callIntelligence'
})
@Injectable()
export class CallIntelligenceState {
  constructor(private callIntelligenceService: CallIntelligenceService) {}

  @Selector()
  static dashboard(state: CallIntelligenceStateModel) {
    return state.dashboard
  }

  @Selector()
  static drilldown(state: CallIntelligenceStateModel) {
    return state.drilldown
  }

  @Selector()
  static leadsListResponse(state: CallIntelligenceStateModel) {
    return state.leadsListResponse
  }

  @Selector()
  static leadDetails(state: CallIntelligenceStateModel) {
    return state.leadDetails
  }

  @Selector()
  static leadAnalysisVersions(state: CallIntelligenceStateModel) {
    return state.leadAnalysisVersions
  }

  @Selector()
  static activeCallTags(state: CallIntelligenceStateModel) {
    return state.activeCallTags
  }

  @Selector()
  static callTags(state: CallIntelligenceStateModel) {
    return state.callTags
  }

  @Selector()
  static callTagsResponse(state: CallIntelligenceStateModel) {
    return state.callTagsResponse
  }

  @Action(GetFilteredCallTags, { cancelUncompleted: true })
  getFilteredCallTags(
    ctx: StateContext<CallIntelligenceStateModel>,
    { payload }: GetFilteredCallTags
  ) {
    return this.callIntelligenceService.getFilteredCallTags(payload).pipe(
      tap((res) => {
        ctx.patchState({ callTagsResponse: res })
      })
    )
  }

  @Action(GetActiveCallTags, { cancelUncompleted: true })
  getActiveCallTags(ctx: StateContext<CallIntelligenceStateModel>) {
    return this.callIntelligenceService.getActiveCallTags().pipe(
      tap((res) => {
        ctx.patchState({ activeCallTags: res?.tags ?? [] })
      })
    )
  }

  @Action(GetCallTags, { cancelUncompleted: true })
  getCallTags(ctx: StateContext<CallIntelligenceStateModel>) {
    return this.callIntelligenceService.getCallTags().pipe(
      tap((res) => {
        ctx.patchState({ callTags: res?.tags ?? [] })
      })
    )
  }

  @Action(CreateCallTag)
  createCallTag(_: StateContext<CallIntelligenceStateModel>, { payload }: CreateCallTag) {
    return this.callIntelligenceService.createCallTag(payload)
  }

  @Action(UpdateCallTag)
  updateCallTag(_: StateContext<CallIntelligenceStateModel>, { id, payload }: UpdateCallTag) {
    return this.callIntelligenceService.updateCallTag(id, payload)
  }

  @Action(DeleteCallTag)
  deleteCallTag(_: StateContext<CallIntelligenceStateModel>, { id }: DeleteCallTag) {
    return this.callIntelligenceService.deleteCallTag(id)
  }

  @Action(RetagCalls)
  retagCalls(_: StateContext<CallIntelligenceStateModel>, { payload }: RetagCalls) {
    return this.callIntelligenceService.retagCalls(payload)
  }

  @Action(GetCallIntelligenceDashboard)
  getCallIntelligenceDashboard(
    ctx: StateContext<CallIntelligenceStateModel>,
    { payload }: GetCallIntelligenceDashboard
  ) {
    ctx.patchState({
      dashboard: undefined
    })
    return this.callIntelligenceService.getCallIntelligenceDashboard(payload).pipe(
      tap((res) => {
        ctx.patchState({
          dashboard: res
        })
      })
    )
  }

  @Action(GetCallIntelligenceDrilldown)
  getCallIntelligenceDrilldown(
    ctx: StateContext<CallIntelligenceStateModel>,
    { payload }: GetCallIntelligenceDashboard
  ) {
    ctx.patchState({
      drilldown: undefined
    })
    return this.callIntelligenceService.getCallIntelligenceDrilldown(payload).pipe(
      tap((res) => {
        ctx.patchState({
          drilldown: res
        })
      })
    )
  }

  @Action(GetCallIntelligenceLeadsList)
  getLeadsList(ctx: StateContext<CallIntelligenceStateModel>, { info }: GetCallIntelligenceLeadsList) {
    ctx.patchState({
      leadsListResponse: undefined
    })
    return this.callIntelligenceService.getLeadsList(info).pipe(
      tap((res) => {
        ctx.patchState({
          leadsListResponse: res
        })
      })
    )
  }

  @Action(GetCallIntelligenceLeadDetails)
  getLeadDetails(ctx: StateContext<CallIntelligenceStateModel>, { id }: GetCallIntelligenceLeadDetails) {
    ctx.patchState({
      leadDetails: undefined
    })
    return this.callIntelligenceService.getLeadDetails(id).pipe(
      tap((res) => {
        ctx.patchState({
          leadDetails: res
        })
      })
    )
  }

  @Action(LinkCallIntelligenceLeadContact)
  linkLeadContact(
    _ctx: StateContext<CallIntelligenceStateModel>,
    { leadId, contactId }: LinkCallIntelligenceLeadContact
  ) {
    return this.callIntelligenceService.linkContact(leadId, contactId)
  }

  @Action(UnlinkCallIntelligenceLeadContact)
  unlinkLeadContact(
    _ctx: StateContext<CallIntelligenceStateModel>,
    { leadId }: UnlinkCallIntelligenceLeadContact
  ) {
    return this.callIntelligenceService.unlinkContact(leadId)
  }

  @Action(MarkCallIntelligenceLeadReviewed)
  markLeadReviewed(
    ctx: StateContext<CallIntelligenceStateModel>,
    { id }: MarkCallIntelligenceLeadReviewed
  ) {
    return this.callIntelligenceService.markLeadReviewed(id).pipe(
      tap((res) => {
        const leadDetails = ctx.getState().leadDetails
        if (leadDetails) {
          ctx.patchState({
            leadDetails: {
              ...leadDetails,
              reviewedDate: res.reviewedDate
            }
          })
        }
      })
    )
  }

  @Action(ReProcessCallIntelligenceLead)
  reProcessCallIntelligenceLead(
    ctx: StateContext<CallIntelligenceStateModel>,
    { id, message }: ReProcessCallIntelligenceLead
  ) {
    return this.callIntelligenceService.reProcessLead(id, message).pipe(
      tap((res) => {
        ctx.patchState({
          leadDetails: res
        })
      })
    )
  }

  @Action(EditCallIntelligenceLeadAnalysis)
  editLeadAnalysis(
    ctx: StateContext<CallIntelligenceStateModel>,
    { id, request }: EditCallIntelligenceLeadAnalysis
  ) {
    return this.callIntelligenceService.editLeadAnalysis(id, request).pipe(
      tap((res) => {
        ctx.patchState({
          leadDetails: res
        })
      })
    )
  }

  @Action(GetCallIntelligenceLeadAnalysisVersions)
  getLeadAnalysisVersions(
    ctx: StateContext<CallIntelligenceStateModel>,
    { id }: GetCallIntelligenceLeadAnalysisVersions
  ) {
    ctx.patchState({
      leadAnalysisVersions: undefined
    })
    return this.callIntelligenceService.getLeadAnalysisVersions(id).pipe(
      tap((res) => {
        ctx.patchState({
          leadAnalysisVersions: res?.versions || []
        })
      })
    )
  }
}
